import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  CloudFunction,
  WebsiteCustomerMessageRequestBody,
  CustomerChannel,
  DialogflowQueryResult,
  LiveChatSMSData,
  CollectionName,
  Conversation,
} from '@tomis-tech/types';

import { Form, Button, FormField, Input } from './common/Form';
import { useSession } from '../context';
import {
  createCustomerUserMessage,
  createFormSubmitUserActionMessage,
} from '../messages';
import { logError } from '.././sentry-iframe';
import { GA4Event } from '../../tracking/event-types';
import { invokeFunction } from '../../cloud-functions';
import { SITE_SLUG } from '../consts';
import SavingOverlay from './common/SavingOverlay';
import { validatePhoneNumber } from '../services/validatePhoneNumber';

import '../styles.css';
import { trackGA4Event } from '../../tracking';
import { PARENT_PAGE_VIEW } from '..';
import { getFirebaseUserId } from '../../firebase';
import { countryCodes } from '../data/countryCodes';

export interface SmsFormProps {}

const SmsForm: React.FC<SmsFormProps> = () => {
  const [userFullName, setUserFullName] = useState('');
  const [userPhoneNumber, setUserPhoneNumber] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [messageSentSuccessfully, setMessageSentSuccessfully] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const { site, conversation } = useSession();

  const [country, setCountry] = useState(() => {
    const defaultCountry =
      countryCodes.find(
        c => c.code.toLowerCase() === site.country?.toLowerCase()
      ) || countryCodes.find(c => c.code.toLowerCase() === 'US'.toLowerCase());

    return {
      name: defaultCountry.name,
      flag: defaultCountry.emoji,
      dialCode: defaultCountry.dial_code,
      code: defaultCountry.code,
    };
  });

  function handleCountryCodeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const country = countryCodes.find(
      c => c.code.toLowerCase() === e.target.value.toLowerCase()
    );

    setCountry({
      name: country?.name,
      flag: country?.emoji,
      dialCode: country?.dial_code,
      code: country?.code,
    });
  }

  const { smsSettings } = site.botConfig;

  if (!smsSettings) return <></>;

  const { webClientSuccessMessage, webClientWelcomeMessage } = smsSettings;
  const botMode = site.currentMode;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function handlePhoneNumberBlur(e: React.FocusEvent<HTMLInputElement>) {
    const validatedNumber = await validatePhoneNumber({
      countryCode: country.code,
      phone: userPhoneNumber,
    });

    if (!validatedNumber.valid || !validatedNumber.data) {
      setErrorMessage(
        validatedNumber.errorMessage ||
          `Phone number could not be validated. Please check the number and try again. ${country.dialCode}${userPhoneNumber}`
      );
      return;
    } else {
      setErrorMessage('');
      // Update input based on Twilio validator function
      setUserPhoneNumber(validatedNumber.data.nationalFormat);
    }
  }

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    e.persist();

    if (!userFullName || !userPhoneNumber || !userMessage || !country.code) {
      // don't let user submit form if fields are empty
      return;
    }

    setIsFetching(true);

    const number = await validatePhoneNumber({
      phone: userPhoneNumber,
      countryCode: country.code,
    });

    if (!number.valid || !number.data) {
      setErrorMessage(
        'Phone number could not be validated. Please check the number and try again.'
      );
      setIsFetching(false);
      return;
    }

    // Update input based on Twilio validator function
    setUserPhoneNumber(number.data.nationalFormat);

    /** Phone number to send text message to */
    const validPhoneNumber = number.data.phoneNumber || userPhoneNumber;
    trackGA4Event(GA4Event.LeadCapturedSMS, { message: userMessage }, true);

    const userActionMessage = createFormSubmitUserActionMessage(
      `${userFullName} requested a text message to: ${validPhoneNumber}`
    );

    await conversation.ref
      .collection(CollectionName.Messages)
      .add(userActionMessage)
      .catch(logError);

    const customerUserMessage = createCustomerUserMessage({
      text: userMessage,
      uid: getFirebaseUserId(),
    });

    const data: LiveChatSMSData = {
      type: 'liveChatSMS',
      fullName: userFullName,
      phoneNumber: validPhoneNumber,
      message: userMessage,
      customerUserMessage,
    };

    const body: WebsiteCustomerMessageRequestBody = {
      customerChannel: CustomerChannel.WebsiteChatbot,
      siteSlug: SITE_SLUG,
      messageId: customerUserMessage.id,
      text: customerUserMessage.text,
      sent: customerUserMessage.sent,
      parentHostname: PARENT_PAGE_VIEW.page.url.hostname,
      conversationId: conversation.ref.id,
      data,
    };

    invokeFunction({
      name: CloudFunction.Api,
      body,
      fullUrl: 'api/webClient/request/sms',
      logToSentry: true,
    })
      .then(
        ([responseBody, response]: [
          {
            useHumanAgent: boolean;
            dialogflowQueryResult: DialogflowQueryResult;
            originalDialogflowQueryResult: DialogflowQueryResult;
            error?: string;
          },
          Response
        ]) => {
          if (response.ok) {
            // show user Success screen
            setMessageSentSuccessfully(true);
            setErrorMessage('');
          } else {
            // error logging is handled by cloud-functions.ts file
            console.error(responseBody.error);
            setMessageSentSuccessfully(false);
            setErrorMessage('Sorry, something went wrong. Please try again');
          }
        }
      )
      .catch(error => {
        setMessageSentSuccessfully(false);
        setErrorMessage('Sorry, something went wrong. Please try again');
        logError(error, { message: 'Error requesting sms chat' });
      })
      .finally(() => setIsFetching(false));
  }

  /** Toggle conversation mode on Conversation to keep chatting with bot */
  async function keepChattingWithChatbot() {
    trackGA4Event(GA4Event.ClickedReturnToChatbot);

    await conversation.ref
      .update({ conversationMode: 'bot' } as Partial<Conversation>)
      .catch(logError);
  }

  const KeepChattingWithChatbot = () => {
    return (
      <KeepChattingContainer>
        <SubtleButton onClick={keepChattingWithChatbot}>
          Return to ChatBot conversation
        </SubtleButton>
      </KeepChattingContainer>
    );
  };

  if (messageSentSuccessfully) {
    return (
      <Container>
        <Padding>
          <SuccessMessageContainer>
            {webClientSuccessMessage}
            <Button
              onClick={() => {
                setMessageSentSuccessfully(false);
                setUserMessage('');
              }}
              style={{ marginTop: '20px' }}
            >
              Text Again
            </Button>
          </SuccessMessageContainer>
          {botMode === 'bot' && <KeepChattingWithChatbot />}
        </Padding>
      </Container>
    );
  }

  const canSubmit =
    userFullName.length >= 1 &&
    userMessage.length >= 1 &&
    userPhoneNumber.length > 8;

  const formFields: FormField[] = [
    {
      label: 'Full Name',
      type: 'text',
      value: userFullName,
      required: true,
      onChange: e => setUserFullName(e.target.value),
      tabIndex: 1,
      autoComplete: 'name',
    },
    {
      tabIndex: 2,
      label: 'Mobile Phone',
      customInput: (
        <PhoneNumberInputContainer>
          <CountryCodeSelect
            onChange={handleCountryCodeChange}
            value={country.code.toLowerCase()}
          >
            {countryCodes.map(c => {
              return (
                <option key={c.code} value={c.code.toLowerCase()}>
                  {c.code} {c.dial_code}
                </option>
              );
            })}
          </CountryCodeSelect>

          <PhoneNumberInputField
            type="tel"
            id="Mobile Phone"
            autoComplete="tel"
            name="phoneNumber"
            tabIndex={2}
            value={userPhoneNumber}
            onChange={e => setUserPhoneNumber(e.target.value)}
            onBlur={handlePhoneNumberBlur}
            required
          />
        </PhoneNumberInputContainer>
      ),
    },
    {
      label: 'Message',
      type: 'text',
      onChange: e => setUserMessage(e.target.value),
      required: true,
      tabIndex: 3,
    },
  ];

  return (
    <Container>
      {isFetching && <SavingOverlay text="Sending your message..." />}
      <Padding>
        <WelcomeMessageContainer>
          {webClientWelcomeMessage}
        </WelcomeMessageContainer>
        <Form
          fields={formFields}
          handleSubmit={handleSubmit}
          disabled={!canSubmit || isFetching}
          submitText="Text Us"
          errorMessage={errorMessage}
          renderAfterFields={
            <ConsentContainer>
              *By pressing text us, you agree to receive text messages from us.
              SMS/Data rates may apply.
            </ConsentContainer>
          }
          buttonProps={{ tabIndex: 4 }}
        />
        {botMode === 'bot' && <KeepChattingWithChatbot />}
      </Padding>
    </Container>
  );
};

export default SmsForm;

export const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Container = styled.div`
  height: 100%;
  width: 100%;
  background: #f2f2f2;
  overflow-y: auto;
`;

export const ConsentContainer = styled.div`
  color: gray;
  text-align: center;
  font-size: 12px;
  margin: 10px 10px 20px 10px;
`;

export const KeepChattingContainer = styled.div`
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
  padding: 12px;
`;

export const SubtleButton = styled.button`
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: gray;
  background: none;
`;

export const Padding = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  width: 100%;
`;

export const SuccessMessageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-top: 20px;
  flex-direction: column;
  line-height: 1.25em;
`;

export const WelcomeMessageContainer = styled.div`
  align-items: center;
  background: white;
  border-radius: 25px;
  box-shadow: 0px 2px 5px lightgrey;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  padding: 20px;
  text-align: center;
`;

export const PhoneNumberInputContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const CountryCodeSelect = styled.select`
  padding: 6px 12px 6px 0px;
  margin-right: 12px;
  border: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url('data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>');
  background-repeat: no-repeat;
  background-position: right 0px center;
  background-size: 24px 24px;
  background-color: transparent;
  color: black;

  &:focus {
    outline: none;
  }
`;

export const PhoneNumberInputField = styled(Input)`
  flex: 1;
`;
